/**
 * Field Rendering Utilities - MA-ARCH-162
 * 
 * Extracted from ScriptPlay.jsx to isolate field type mapping and validation logic.
 * These utilities handle the complex field rendering decisions.
 */

/**
 * Validation functions
 */
export const validateEmail = (email) => {
  if (!email) return true; // Empty is OK (required check is separate)
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

export const validatePhone = (phone) => {
  if (!phone) return true; // Empty is OK (required check is separate)
  // Allow various phone formats: digits, spaces, dashes, parentheses, plus sign
  const cleaned = phone.replace(/[\s\-\(\)\+\.]/g, "");
  return /^\d{7,15}$/.test(cleaned);
};

export const getValidationError = (field, value) => {
  if (!value && field.required) {
    return "This field is required";
  }
  if (field.field_type === "email" && value && !validateEmail(value)) {
    return "Please enter a valid email address";
  }
  if (field.field_type === "phone" && value && !validatePhone(value)) {
    return "Please enter a valid phone number";
  }
  if (field.field_type === "number" && value) {
    const cleaned = String(value).trim();
    if (cleaned && !/^[-+]?\d*(\.\d+)?$/.test(cleaned)) {
      return "Please enter a valid number";
    }
  }
  return null;
};

/**
 * Field type labels for display
 */
export const FIELD_TYPE_LABELS = {
  text: "Text",
  phone: "Phone",
  email: "Email",
  number: "Number",
  textarea: "Long text",
  checkbox: "Checkbox",
  dropdown: "Dropdown",
};

/**
 * Get the input type for HTML input element
 */
export const getInputType = (fieldType) => {
  switch (fieldType) {
    case "email":
      return "email";
    case "phone":
      return "tel";
    case "number":
      return "text"; // use text with inputMode for better mobile UX
    default:
      return "text";
  }
};

/**
 * Get render key for field (handles prospect-scoped fields)
 */
export const getFieldRenderKey = (field, instanceProspectId) => {
  const scope = field.scope || "household";
  return scope === "prospects" && instanceProspectId
    ? `${field.id}::${instanceProspectId}`
    : field.id;
};

/**
 * Get default subject for field (who the field applies to)
 */
export const getDefaultFieldSubject = (field, instanceProspectId) => {
  const scope = field.scope || "household";
  return scope === "prospects" && instanceProspectId
    ? instanceProspectId
    : "household";
};

/**
 * Get display label for a field
 */
export const getFieldDisplayLabel = (field) => {
  return String(field?.label ?? field?.name ?? field?.title ?? "").trim() || "Field";
};

/**
 * Normalize field value for number input
 */
export const normalizeNumberInput = (value) => {
  return String(value).replace(/[^0-9.\-+]/g, "");
};

/**
 * Check if field passes the prospect filter
 */
export const passesProspectFilter = (selectedSubject, prospectInfoFilter, isInline) => {
  if (isInline) return true;
  if (prospectInfoFilter === "all") return true;
  return selectedSubject === prospectInfoFilter;
};
